import { StandardFonts } from "pdf-lib";

/**
 * Map PDF / pdf.js font metadata to pdf-lib standard fonts and CSS preview stacks.
 */
export const resolvePdfFontKey = (originalFontName = "", stylesFontFamily = "") => {
    const name = String(originalFontName || "").toLowerCase();
    const family = String(stylesFontFamily || "").toLowerCase();

    const isMono =
        name.includes("courier") ||
        name.includes("mono") ||
        family.includes("monospace");
    const isSerif =
        !isMono &&
        (name.includes("times") ||
            name.includes("serif") ||
            name.includes("georgia") ||
            name.includes("garamond") ||
            family.includes("serif"));
    const isBold =
        name.includes("bold") ||
        name.includes(",bold") ||
        name.endsWith("-b") ||
        /\bbold\b/.test(name);
    const isItalic =
        name.includes("italic") ||
        name.includes("oblique") ||
        name.includes(",italic") ||
        name.endsWith("-i") ||
        /\bitalic\b/.test(name);

    if (isMono) {
        if (isBold && isItalic) return "CourierBoldOblique";
        if (isBold) return "CourierBold";
        if (isItalic) return "CourierOblique";
        return "Courier";
    }
    if (isSerif) {
        if (isBold && isItalic) return "TimesBoldItalic";
        if (isBold) return "TimesBold";
        if (isItalic) return "TimesItalic";
        return "TimesRoman";
    }
    if (isBold && isItalic) return "HelveticaBoldOblique";
    if (isBold) return "HelveticaBold";
    if (isItalic) return "HelveticaOblique";
    return "Helvetica";
};

const STANDARD_FONT_BY_KEY = {
    Helvetica: StandardFonts.Helvetica,
    HelveticaBold: StandardFonts.HelveticaBold,
    HelveticaOblique: StandardFonts.HelveticaOblique,
    HelveticaBoldOblique: StandardFonts.HelveticaBoldOblique,
    TimesRoman: StandardFonts.TimesRoman,
    TimesBold: StandardFonts.TimesRomanBold,
    TimesItalic: StandardFonts.TimesRomanItalic,
    TimesBoldItalic: StandardFonts.TimesRomanBoldItalic,
    Courier: StandardFonts.Courier,
    CourierBold: StandardFonts.CourierBold,
    CourierOblique: StandardFonts.CourierOblique,
    CourierBoldOblique: StandardFonts.CourierBoldOblique,
};

export const standardFontForKey = (pdfFontKey) =>
    STANDARD_FONT_BY_KEY[pdfFontKey] || StandardFonts.Helvetica;

const CSS_STACK_BY_KEY = {
    Helvetica: 'Helvetica, Arial, sans-serif',
    HelveticaBold: 'Helvetica, Arial, sans-serif',
    HelveticaOblique: 'Helvetica, Arial, sans-serif',
    HelveticaBoldOblique: 'Helvetica, Arial, sans-serif',
    TimesRoman: '"Times New Roman", Times, serif',
    TimesBold: '"Times New Roman", Times, serif',
    TimesItalic: '"Times New Roman", Times, serif',
    TimesBoldItalic: '"Times New Roman", Times, serif',
    Courier: 'Courier, "Courier New", monospace',
    CourierBold: 'Courier, "Courier New", monospace',
    CourierOblique: 'Courier, "Courier New", monospace',
    CourierBoldOblique: 'Courier, "Courier New", monospace',
};

export const cssFontStackForKey = (pdfFontKey) =>
    CSS_STACK_BY_KEY[pdfFontKey] || CSS_STACK_BY_KEY.Helvetica;

export const cssFontWeightForKey = (pdfFontKey) =>
    pdfFontKey.includes("Bold") ? "700" : "400";

export const cssFontStyleForKey = (pdfFontKey) =>
    pdfFontKey.includes("Italic") || pdfFontKey.includes("Oblique") ? "italic" : "normal";

/** After getOperatorList(), read embedded font PostScript name from pdf.js page. */
export const getOriginalFontNameFromPage = (page, fontName) => {
    if (!page || !fontName) return "";
    try {
        const fontObj = page.commonObjs?.get?.(fontName);
        return String(fontObj?.name || fontObj?.loadedName || "").trim();
    } catch {
        return "";
    }
};

export const NEW_TEXT_FONT_OPTIONS = [
    { value: "Helvetica", label: "Helvetica" },
    { value: "HelveticaBold", label: "Helvetica Bold" },
    { value: "HelveticaOblique", label: "Helvetica Italic" },
    { value: "TimesRoman", label: "Times New Roman" },
    { value: "TimesBold", label: "Times Bold" },
    { value: "TimesItalic", label: "Times Italic" },
    { value: "Courier", label: "Courier" },
    { value: "CourierBold", label: "Courier Bold" },
];

export const fontMetaFromPdfKey = (pdfFontKey = "Helvetica") => ({
    pdfFontKey,
    cssFontFamily: cssFontStackForKey(pdfFontKey),
    cssFontWeight: cssFontWeightForKey(pdfFontKey),
    cssFontStyle: cssFontStyleForKey(pdfFontKey),
});

export const buildFontMetaForItem = (page, item, styles = {}) => {
    const fontName = String(item?.fontName || "");
    const styleEntry = styles[fontName] || {};
    const originalFontName = getOriginalFontNameFromPage(page, fontName);
    const pdfFontKey = resolvePdfFontKey(
        originalFontName,
        styleEntry.fontFamily || ""
    );
    return {
        fontName,
        originalFontName,
        pdfFontKey,
        cssFontFamily: cssFontStackForKey(pdfFontKey),
        cssFontWeight: cssFontWeightForKey(pdfFontKey),
        cssFontStyle: cssFontStyleForKey(pdfFontKey),
    };
};
